import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { ZOOD_MASCOT } from "@/lib/brand";
import { PublicNavMobile } from "@/components/PublicNavMobile";
import {
  Sparkles, ArrowRight, LogIn, BookOpen, PenLine, Calculator, FlaskConical, Languages,
  Hand, Volume2, MousePointerClick, Beaker, Star, Trophy, Zap, ShieldCheck,
  Heart, Users, GraduationCap, Ticket, Loader2, CheckCircle2,
} from "lucide-react";

const SUBJECTS = [
  { icon: BookOpen, name: "Reading & Phonics", color: "#0284C7", desc: "Sound out words, blend and read with meaning." },
  { icon: Sparkles, name: "Vocabulary", color: "#7A3FF2", desc: "Grow word power with matching and meanings." },
  { icon: PenLine, name: "Writing & Cursive", color: "#0D9488", desc: "Trace letters, words and sentences by hand." },
  { icon: Calculator, name: "Math", color: "#FF9E1B", desc: "Count, add and solve with tap-along objects." },
  { icon: FlaskConical, name: "Science", color: "#10B981", desc: "Predict, test and explore real experiments." },
  { icon: Languages, name: "Foreign Language", color: "#DB2777", desc: "First words and phrases in Spanish and French." },
];

const ENGINES = [
  { icon: Hand, title: "Handwriting Tracer", text: "Trace strokes with finger, stylus or mouse — scored in real time.", color: "#0D9488" },
  { icon: MousePointerClick, title: "Tap-to-Count Math", text: "Kids tap and combine objects to build number sense.", color: "#FF9E1B" },
  { icon: Volume2, title: "Phonics Read-Aloud", text: "Every word and sound speaks — powered by natural audio.", color: "#0284C7" },
  { icon: Beaker, title: "Sink-or-Float Lab", text: "Make a prediction, drop it in the water, see what happens.", color: "#10B981" },
];

const REWARDS = [
  { icon: Zap, title: "Points", text: "Earn points for every lesson finished." },
  { icon: Star, title: "Subject Stickers", text: "Collect a sticker for completing each subject." },
  { icon: Trophy, title: "Badges & Streaks", text: "Unlock badges that keep kids coming back." },
];

const GRADES = ["PreK", "K", "1st", "2nd", "3rd", "4th", "5th", "6th", "7th", "8th"];

function BrandedNav({ brand, accent }) {
  const linkClass = "block rounded-xl px-3 py-3 font-heading text-base font-semibold text-[#1F2937] hover:bg-[#F2ECE1]";
  const links = [
    ["#subjects", "Subjects"],
    ["#engines", "Interactive"],
    ["#rewards", "Rewards"],
    ["#redeem", "Have a code?"],
    ["/login", "Log In"],
  ];
  const items = links.map(([href, label]) => ({
    label, key: href,
    node: href.startsWith("/")
      ? <Link to={href} className={linkClass}>{label}</Link>
      : <a href={href} className={linkClass}>{label}</a>,
  }));
  return (
    <header className="sticky top-0 z-40 border-b border-[#E5DECF] bg-[#FAF7F2]/90 backdrop-blur-md">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
        <div className="flex items-center gap-2.5">
          <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-2xl text-white" style={{ background: accent }}>
            {brand.logo_url ? <img src={brand.logo_url} alt="" className="h-full w-full object-cover" /> : <Sparkles size={20} />}
          </div>
          <div className="leading-tight">
            <div className="font-heading text-base font-extrabold text-[#1F2937]" data-testid="branded-name">{brand.name}</div>
            <div className="font-body text-[10px] uppercase tracking-widest text-[#94A3B8]">Powered by ZoodLeap</div>
          </div>
        </div>
        <nav className="hidden items-center gap-1 md:flex">
          {[["#subjects", "Subjects"], ["#engines", "Interactive"], ["#rewards", "Rewards"], ["#redeem", "Have a code?"]].map(([href, label]) => (
            <a key={href} href={href} className="rounded-full px-4 py-2 text-sm font-semibold text-[#1F2937] hover:bg-[#F2ECE1]">{label}</a>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <Link to="/login" data-testid="branded-nav-login" className="hidden rounded-full px-4 py-2 text-sm font-bold text-[#1F2937] hover:bg-[#F2ECE1] sm:inline-flex">Log In</Link>
          <Link to={`/signup?slug=${brand.slug}`} data-testid="branded-signup"
            className="hidden rounded-full px-4 py-2 text-sm font-bold text-white shadow-sm transition-all hover:brightness-95 active:translate-y-0.5 md:inline-flex"
            style={{ background: accent }}>
            Get Started
          </Link>
          <PublicNavMobile
            accent={accent}
            items={items}
            cta={
              <Link
                to={`/signup?slug=${brand.slug}`}
                data-testid="branded-signup-mobile"
                className="block rounded-2xl px-5 py-3 text-center font-heading text-base font-bold text-white shadow-sm"
                style={{ background: accent }}
              >
                Get Started
              </Link>
            }
          />
        </div>
      </div>
    </header>
  );
}

export default function ResellerLanding() {
  const { slug } = useParams();
  const [brand, setBrand] = useState(undefined);
  const [plans, setPlans] = useState([]);
  const [checkoutReady, setCheckoutReady] = useState(false);
  const [subscribing, setSubscribing] = useState({});

  useEffect(() => {
    api.brandBySlug(slug).then(setBrand).catch(() => setBrand(null));
    api.publicResellerPlans(slug).then((r) => { setPlans(r.plans || []); setCheckoutReady(!!r.checkout_ready); }).catch(() => setPlans([]));
  }, [slug]);

  const money = (cents, cur = "usd") => new Intl.NumberFormat("en-US",
    { style: "currency", currency: (cur || "usd").toUpperCase(), maximumFractionDigits: 2 }).format((cents || 0) / 100);

  const subscribe = async (planId) => {
    setSubscribing((s) => ({ ...s, [planId]: true }));
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        window.location.href = `/signup?slug=${slug}&plan=${planId}`;
        return;
      }
      const r = await api.startResellerPlanCheckout(slug, planId, window.location.origin);
      window.location.href = r.checkout_url;
    } catch (e) {
      alert((e?.response?.data?.detail) || "Couldn't start checkout. Please try again.");
    } finally {
      setSubscribing((s) => ({ ...s, [planId]: false }));
    }
  };

  if (brand === undefined) return <div className="p-16 text-center"><Loader2 className="mx-auto animate-spin text-[#0D9488]" /></div>;
  if (brand === null) return (
    <div className="p-16 text-center">
      <h1 className="font-heading text-2xl font-bold text-[#1F2937]">Brand not found</h1>
      <Link to="/" className="mt-4 inline-block font-bold text-[#0D9488] hover:underline">Go to ZoodLeap</Link>
    </div>
  );

  const accent = brand.accent_color || "#0D9488";
  const brandedSignup = `/signup?slug=${brand.slug}`;

  return (
    <div id="top" className="min-h-screen bg-[#FAF7F2] text-[#1F2937]" data-testid="reseller-landing">
      <BrandedNav brand={brand} accent={accent} />

      {/* Hero */}
      <section className="mx-auto grid max-w-7xl items-center gap-10 px-4 py-14 sm:px-6 lg:grid-cols-2 lg:py-20 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <div className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-widest"
            style={{ background: `${accent}1A`, color: accent }} data-testid="hero-badge">
            <Sparkles size={14} /> The complete PreK–8th learning platform
          </div>
          <h1 className="mt-4 font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
            Learning powered by <span style={{ color: accent }} data-testid="branded-hero-name">{brand.name}</span>.
          </h1>
          <p className="mt-5 font-body text-base sm:text-lg text-[#6B7280] max-w-xl">
            Reading, phonics, vocabulary, handwriting, math, science and foreign language —
            a playful, interactive world for ages 5–14, curated for {brand.name} families and classrooms.
          </p>
          <div className="mt-6 flex flex-wrap gap-2">
            {GRADES.map((g) => (
              <span key={g} className="rounded-full border border-[#E5DECF] bg-white px-3 py-1 text-xs font-bold text-[#6B7280]">{g}</span>
            ))}
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to={brandedSignup} data-testid="hero-get-started"
              className="inline-flex items-center gap-2 rounded-2xl px-6 py-4 text-base font-bold text-white shadow-md transition-all hover:brightness-95 hover:shadow-lg active:translate-y-0.5"
              style={{ background: accent }}>
              Get Started <ArrowRight size={18} />
            </Link>
            <Link to="/student-login" data-testid="hero-student-login"
              className="inline-flex items-center gap-2 rounded-2xl bg-white border-2 border-[#E5DECF] px-6 py-4 text-base font-bold text-[#1F2937] transition-all hover:border-[#0D9488] active:translate-y-0.5">
              <LogIn size={18} /> I'm a Student
            </Link>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.15 }}
          className="relative flex items-center justify-center">
          <div className="absolute inset-0 -z-0 mx-auto h-[85%] w-[85%] self-center rounded-full blur-3xl"
            style={{ background: `radial-gradient(circle, ${accent}33 0%, ${accent}11 60%, transparent 100%)` }} />
          <motion.img
            src={ZOOD_MASCOT} alt="Zoodle mascot" data-testid="hero-mascot"
            className="relative z-10 w-[78%] max-w-[440px] drop-shadow-[0_20px_40px_rgba(23,190,187,0.25)]"
            animate={{ y: [0, -14, 0] }} transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }} />
          <div className="absolute -bottom-2 -left-2 z-20 hidden rounded-2xl border border-[#E5DECF] bg-white px-4 py-3 shadow-lg sm:flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-full text-white" style={{ background: accent }}><Star size={18} /></div>
            <div className="leading-tight">
              <div className="font-heading text-sm font-bold">Sticker earned!</div>
              <div className="font-body text-xs text-[#6B7280]">Science Explorer</div>
            </div>
          </div>
          <div className="absolute -top-1 right-0 z-20 hidden rounded-2xl border border-[#E5DECF] bg-white px-4 py-3 shadow-lg sm:flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#FF9E1B] text-white"><Zap size={18} /></div>
            <div className="leading-tight">
              <div className="font-heading text-sm font-bold">+50 points</div>
              <div className="font-body text-xs text-[#6B7280]">Lesson complete!</div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Redeem-a-code CTA banner — reseller-specific, keeps the "get access" story front and center */}
      <section id="redeem" className="mx-auto max-w-7xl px-4 pb-4 sm:px-6 lg:px-8">
        <div className="flex flex-col items-start gap-4 rounded-[28px] border border-[#E5DECF] bg-white p-6 sm:flex-row sm:items-center sm:justify-between sm:p-7"
          data-testid="redeem-banner">
          <div className="flex items-start gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl text-white" style={{ background: accent }}>
              <Ticket size={22} />
            </div>
            <div>
              <div className="font-heading text-lg font-extrabold text-[#1F2937]">Have a code from {brand.name}?</div>
              <p className="mt-0.5 font-body text-sm text-[#6B7280]">
                Redeem it on signup to unlock the full curriculum for your family or classroom — no card needed.
              </p>
            </div>
          </div>
          <Link to={brandedSignup} data-testid="redeem-cta"
            className="inline-flex shrink-0 items-center gap-2 rounded-2xl px-5 py-3 text-sm font-bold text-white shadow-sm transition-all hover:brightness-95 active:translate-y-0.5"
            style={{ background: accent }}>
            Redeem a code <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* Reseller-defined subscription plans — only shown if the reseller has active plans */}
      {plans.length > 0 && (
        <section id="plans" className="mx-auto max-w-7xl px-4 pt-4 pb-6 sm:px-6 lg:px-8" data-testid="reseller-plans-section">
          <div className="text-center">
            <div className="text-xs uppercase tracking-widest font-bold" style={{ color: accent }}>Choose a plan</div>
            <h2 className="mt-2 font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">Subscribe with {brand.name}</h2>
            <p className="mx-auto mt-2 max-w-2xl font-body text-sm text-[#6B7280]">
              Payments go straight to {brand.name} — you'll be redirected to their secure Stripe checkout.
            </p>
          </div>
          <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {plans.map((p, i) => (
              <motion.div key={p.id} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
                data-testid={`plan-card-${p.id}`}
                className="flex flex-col rounded-3xl border-2 bg-white p-6" style={{ borderColor: `${accent}33` }}>
                <div className="font-heading text-xl font-extrabold text-[#1F2937]">{p.name}</div>
                {p.description && <p className="mt-1 font-body text-sm text-[#6B7280]">{p.description}</p>}
                <div className="mt-4">
                  <span className="font-heading text-4xl font-extrabold" style={{ color: accent }}>{money(p.amount_cents, p.currency)}</span>
                  <span className="ml-1 font-body text-sm text-[#6B7280]">/{p.interval === "year" ? "yr" : "mo"}</span>
                </div>
                <div className="mt-4 space-y-2">
                  {(() => {
                    const seatsBullet = `Up to ${p.max_students || 30} ${(p.max_students || 30) <= 10 ? "children" : "students"}`;
                    const custom = (p.features || []).filter(Boolean);
                    const list = custom.length > 0
                      ? [seatsBullet, ...custom]
                      : [seatsBullet, "Full PreK–8 curriculum", "Progress reports & stickers", "New lessons added regularly"];
                    return list.slice(0, 6).map((f) => (
                      <div key={f} className="flex items-start gap-2 font-body text-sm text-[#1F2937]">
                        <CheckCircle2 size={16} style={{ color: accent }} className="mt-0.5 shrink-0" /> <span>{f}</span>
                      </div>
                    ));
                  })()}
                </div>
                <button onClick={() => subscribe(p.id)} disabled={!checkoutReady || subscribing[p.id]}
                  data-testid={`plan-subscribe-${p.id}`}
                  className="mt-6 inline-flex items-center justify-center gap-2 rounded-2xl px-5 py-3 font-heading text-sm font-bold text-white shadow-sm transition-all hover:brightness-95 active:translate-y-0.5 disabled:opacity-50"
                  style={{ background: accent }}>
                  {subscribing[p.id] ? <Loader2 size={16} className="animate-spin" /> : (checkoutReady ? <>Subscribe <ArrowRight size={16} /></> : <>Coming soon</>)}
                </button>
                {!checkoutReady && <p className="mt-2 text-center font-body text-[11px] text-[#94A3B8]">Waiting for {brand.name} to connect their payment account.</p>}
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* Subjects */}
      <section id="subjects" className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="text-center">
          <div className="text-xs uppercase tracking-widest font-bold" style={{ color: accent }}>Six subjects, one academy</div>
          <h2 className="mt-2 font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">Everything they need to grow</h2>
        </div>
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {SUBJECTS.map((s, i) => (
            <motion.div key={s.name} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
              whileHover={{ y: -4 }} data-testid={`subject-showcase-${i}`}
              className="rounded-3xl border border-[#E5DECF] bg-white p-6" style={{ boxShadow: "0 8px 30px rgb(59,66,159,0.05)" }}>
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl text-white" style={{ background: s.color }}><s.icon size={28} /></div>
              <h3 className="mt-4 font-heading text-xl font-bold">{s.name}</h3>
              <p className="mt-1 font-body text-sm text-[#6B7280]">{s.desc}</p>
            </motion.div>
          ))}
          <div className="flex flex-col items-start justify-center rounded-3xl border-2 border-dashed border-[#E5DECF] bg-[#F2ECE1]/40 p-6">
            <div className="font-heading text-lg font-bold" style={{ color: accent }}>Now covering PreK through 8th</div>
            <p className="mt-1 font-body text-sm text-[#6B7280]">Reading, vocabulary, math and science lessons across every grade, PreK–8th.</p>
          </div>
        </div>
      </section>

      {/* Interactive engines (dark section) */}
      <section id="engines" className="bg-[#1F2937] py-16 text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="text-xs uppercase tracking-widest font-bold" style={{ color: accent }}>Learning by doing</div>
            <h2 className="mt-2 font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">Real interactive activities</h2>
            <p className="mx-auto mt-3 max-w-2xl font-body text-white/70">Not videos to watch — hands-on engines kids actually play with.</p>
          </div>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {ENGINES.map((e, i) => (
              <motion.div key={e.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
                className="rounded-3xl bg-white/5 p-6 ring-1 ring-white/10">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl text-white" style={{ background: e.color }}><e.icon size={24} /></div>
                <h3 className="mt-4 font-heading text-lg font-bold">{e.title}</h3>
                <p className="mt-1 font-body text-sm text-white/70">{e.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Rewards */}
      <section id="rewards" className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid items-center gap-10 lg:grid-cols-2">
          <div>
            <div className="text-xs uppercase tracking-widest font-bold" style={{ color: accent }}>Motivation built in</div>
            <h2 className="mt-2 font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">Points, stickers &amp; badges kids love</h2>
            <p className="mt-3 font-body text-[#6B7280]">Every lesson rewards effort. Kids build a sticker book, earn points and unlock badges as they master each subject.</p>
            <div className="mt-6 space-y-4">
              {REWARDS.map((r) => (
                <div key={r.title} className="flex items-start gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl" style={{ background: `${accent}1A`, color: accent }}><r.icon size={20} /></div>
                  <div><div className="font-heading font-bold">{r.title}</div><p className="font-body text-sm text-[#6B7280]">{r.text}</p></div>
                </div>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[["Reading Star", "#0284C7"], ["Word Wizard", "#7A3FF2"], ["Math Whiz", "#FF9E1B"], ["Science Explorer", "#10B981"]].map(([name, c]) => (
              <div key={name} className="flex flex-col items-center rounded-3xl border-2 bg-white p-6 text-center" style={{ borderColor: c }}>
                <div className="flex h-16 w-16 items-center justify-center rounded-full text-white" style={{ background: c }}><Star size={30} /></div>
                <div className="mt-3 font-heading text-sm font-bold">{name}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust */}
      <section className="bg-[#F2ECE1]/50 py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { icon: ShieldCheck, t: "Privacy-first", d: "No ads, no trackers. COPPA & FERPA aligned." },
              { icon: Heart, t: "Kind feedback", d: "Encouraging, never 'wrong' — kids stay confident." },
              { icon: Users, t: "Home & school", d: "Parents, teachers and school admins all supported." },
              { icon: GraduationCap, t: "Standards-minded", d: "Built around core PreK–8 skills and progressions." },
            ].map((x, i) => (
              <motion.div key={x.t} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
                className="rounded-3xl border border-[#E5DECF] bg-white p-6">
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl" style={{ background: `${accent}1A`, color: accent }}><x.icon size={22} /></div>
                <div className="mt-3 font-heading font-bold">{x.t}</div>
                <p className="mt-1 font-body text-sm text-[#6B7280]">{x.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="mx-auto max-w-7xl px-4 pb-16 pt-16 sm:px-6 lg:px-8">
        <div className="rounded-[32px] p-8 sm:p-12 text-center text-white"
          style={{ background: `linear-gradient(135deg, ${accent} 0%, #7A3FF2 100%)` }}>
          <h2 className="font-heading text-2xl sm:text-3xl font-extrabold">Ready to light up learning with {brand.name}?</h2>
          <p className="mt-2 font-body text-white/85">Create a free account with your code and get started in minutes.</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link to={brandedSignup} data-testid="cta-signup"
              className="inline-flex items-center gap-2 rounded-2xl bg-white px-6 py-4 font-bold shadow-md transition-all hover:brightness-95 active:translate-y-0.5"
              style={{ color: accent }}>
              Get Started <ArrowRight size={18} />
            </Link>
            <Link to="/login" data-testid="cta-login"
              className="inline-flex items-center gap-2 rounded-2xl bg-white/10 px-6 py-4 font-bold text-white ring-1 ring-white/30 transition-all hover:bg-white/20 active:translate-y-0.5">
              Log In
            </Link>
          </div>
        </div>
      </section>

      <footer className="bg-[#1F2937] py-10 text-center">
        <div className="mx-auto max-w-7xl px-4">
          <div className="font-heading text-lg font-extrabold text-white" data-testid="footer-brand">{brand.name}</div>
          <p className="mt-2 font-body text-sm text-white/60">The complete PreK–8th interactive learning platform · Reading · Vocabulary · Writing · Math · Science · Foreign Language</p>
          <p className="mt-4 font-body text-xs text-white/40">Powered by ZoodLeap · Privacy-first · COPPA &amp; FERPA aligned</p>
        </div>
      </footer>
    </div>
  );
}
